/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { Diagnostics } from "../harness/diagnostics.js";
import { extractError } from "../harness/error.js";
import { createRunStateManager } from "../run/index.js";
import { runGraph } from "../run/run-graph.js";
import { streamsToAsyncIterable, stubOutStreams, } from "../stream.js";
import { timestamp } from "../timestamp.js";
export class RunServer {
    #transport;
    constructor(transport) {
        this.#transport = transport;
    }
    async serve(runner, diagnostics = false, context = {}) {
        const stream = this.#transport.createServerStream();
        const requestReader = stream.readableRequests.getReader();
        let request = await requestReader.read();
        if (request.done)
            return;
        const responses = stream.writableResponses.getWriter();
        const servingContext = {
            ...context,
            state: createRunStateManager(),
            probe: diagnostics
                ? new Diagnostics(async (message) => {
                    const { type, data } = message;
                    const response = [type, stubOutStreams(data)];
                    if (type == "nodestart") {
                        response.push(message.result);
                    }
                    await responses.write(response);
                })
                : undefined,
        };
        try {
            for await (const stop of runGraph({ graph: runner }, servingContext)) {
                if (stop.type === "input") {
                    const { node, inputArguments, timestamp, path, invocationId } = stop;
                    const bubbled = invocationId == -1;
                    await responses.write([
                        "input",
                        { node, inputArguments, timestamp, path, bubbled },
                    ]);
                    request = await requestReader.read();
                    if (request.done) {
                        await responses.close();
                        return;
                    }
                    else {
                        const [type, inputs] = request.value;
                        if (type === "input") {
                            stop.inputs = inputs.inputs;
                        }
                    }
                }
                else if (stop.type === "output") {
                    const { node, outputs, timestamp, path, invocationId } = stop;
                    const bubbled = invocationId == -1;
                    await responses.write([
                        "output",
                        { node, outputs, timestamp, path, bubbled },
                    ]);
                }
            }
            await responses.write(["end", { timestamp: timestamp() }]);
            await responses.close();
        }
        catch (e) {
            const error = extractError(e);
            console.error("Run Server error:", error);
            await responses.write(["error", { error, timestamp: timestamp() }]);
            await responses.close();
        }
    }
}
const createRunResult = (response) => {
    const [type, data, state] = response.data;
    const reply = async (chunk) => {
        if (type !== "input") {
            throw new Error("For now, we cannot reply to messages other than 'input'.");
        }
        await response.reply([type, chunk]);
    };
    return {
        type,
        data,
        state,
        reply,
    };
};
export class RunClient {
    #transport;
    constructor(clientTransport) {
        this.#transport = clientTransport;
    }
    async *run(state) {
        const stream = this.#transport.createClientStream();
        const server = streamsToAsyncIterable(stream.writableRequests, stream.readableResponses);
        const request = ["run", {}];
        state && request.push(state);
        await server.start(request);
        for await (const response of server) {
            yield createRunResult(response);
        }
    }
    async runOnce(inputs) {
        let outputs;
        for await (const stop of this.run()) {
            const { type, data } = stop;
            if (type === "input") {
                stop.reply({ inputs });
            }
            else if (type === "output") {
                outputs = data.outputs;
                break;
            }
        }
        return outputs || {};
    }
}
//# sourceMappingURL=run.js.map